import type {
  AdminTournamentScheduleConfig,
  AdminTournamentScheduleSlot,
} from "../services/eventRegistrationAdminService";
import { UNASSIGNED_COURT } from "./tournamentOperationTypes";

export function timelineStatusValue(slot: {
  status?: string | null;
  matchStatus?: string | null;
}) {
  const matchStatus = String(slot.matchStatus || "")
    .trim()
    .toLowerCase();
  if (matchStatus && matchStatus !== "pending") return matchStatus;
  return String(slot.status || matchStatus || "scheduled")
    .trim()
    .toLowerCase();
}

export function timelineStatusClass(status?: string | null) {
  const normalized = String(status || "")
    .trim()
    .toLowerCase();
  if (normalized === "finished") return "is-finished";
  if (normalized === "in_progress") return "is-in-progress";
  return "is-scheduled";
}

export function slotMatchId(slot: AdminTournamentScheduleSlot) {
  const rawMatchId =
    slot.matchId ??
    (slot as AdminTournamentScheduleSlot & { match_id?: number | null })
      .match_id;
  const matchId = Number(rawMatchId || 0);
  return Number.isFinite(matchId) && matchId > 0 ? matchId : 0;
}

export function toDateTimeLocal(value?: string | null) {
  if (!value) return "";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return "";
  const offset = date.getTimezoneOffset() * 60000;
  return new Date(date.getTime() - offset).toISOString().slice(0, 16);
}

export function fromDateTimeLocal(value: string) {
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return "";
  return date.toISOString();
}

export function dateOnlyFromLocal(value: string) {
  return String(value || "").slice(0, 10);
}

export function localDateDayIndex(value: string) {
  const [year, month, day] = dateOnlyFromLocal(value)
    .split("-")
    .map((item) => Number(item));
  if (!year || !month || !day) return null;
  return Math.floor(Date.UTC(year, month - 1, day) / (24 * 60 * 60 * 1000));
}

export function calendarDaysInclusive(startValue: string, endValue: string) {
  const startIndex = localDateDayIndex(startValue);
  const endIndex = localDateDayIndex(endValue);
  if (startIndex === null || endIndex === null) return 1;
  return Math.max(1, endIndex - startIndex + 1);
}

export function timeOnlyFromLocal(value: string, fallback: string) {
  return String(value || "").slice(11, 16) || fallback;
}

export function localDateTimeIso(dateValue: string, timeValue: string) {
  return fromDateTimeLocal(`${dateValue}T${timeValue}`);
}

export function addLocalDays(dateValue: string, days: number) {
  const date = new Date(`${dateValue}T00:00`);
  if (Number.isNaN(date.getTime())) return dateValue;
  date.setDate(date.getDate() + Math.max(0, days));
  return toDateTimeLocal(date.toISOString()).slice(0, 10);
}

export function weekdayKeyFromDate(dateValue: string) {
  const keys = [
    "sunday",
    "monday",
    "tuesday",
    "wednesday",
    "thursday",
    "friday",
    "saturday",
  ];
  const date = new Date(`${dateValue}T00:00`);
  return keys[Number.isNaN(date.getTime()) ? 0 : date.getDay()];
}

export type ScheduleDayWindowDraft = {
  date: string;
  startTime: string;
  endTime: string;
};

export type ScheduleBreakPeriodDraft = {
  id: string;
  label: string;
  date: string;
  startTime: string;
  endTime: string;
};

function createBreakId(index: number) {
  return `break-${index + 1}`;
}

export function buildScheduleDayWindows({
  daysCount,
  firstDay,
  fallbackEndTime = "18:00",
  fallbackStartTime = "08:00",
  previous = [],
}: {
  daysCount: number;
  firstDay: string;
  fallbackEndTime?: string;
  fallbackStartTime?: string;
  previous?: ScheduleDayWindowDraft[];
}) {
  const safeDaysCount = Math.max(1, Math.floor(Number(daysCount || 1)));
  return Array.from({ length: safeDaysCount }, (_item, index) => {
    const date = addLocalDays(firstDay, index);
    const previousForDate = previous.find((item) => item.date === date);
    const previousByIndex = previous[index];
    return {
      date,
      startTime:
        previousForDate?.startTime ||
        previousByIndex?.startTime ||
        fallbackStartTime,
      endTime:
        previousForDate?.endTime || previousByIndex?.endTime || fallbackEndTime,
    };
  });
}

export function createScheduleDraft(
  config?: AdminTournamentScheduleConfig | null,
) {
  const now = new Date();
  const start = config?.dayStartAt || now.toISOString();
  const end =
    config?.dayEndAt ||
    new Date(now.getTime() + 8 * 60 * 60 * 1000).toISOString();
  const courts =
    Array.isArray(config?.courtNames) && config.courtNames.length
      ? config.courtNames
      : [];
  const startLocal = toDateTimeLocal(start);
  const endLocal = toDateTimeLocal(end);
  const firstDay = dateOnlyFromLocal(startLocal);
  const daysCount = calendarDaysInclusive(startLocal, endLocal);
  const fallbackStartTime =
    config?.operatingHours?.[0]?.startTime ||
    timeOnlyFromLocal(startLocal, "08:00");
  const fallbackEndTime =
    config?.operatingHours?.[0]?.endTime || timeOnlyFromLocal(endLocal, "18:00");
  const daySchedules = buildScheduleDayWindows({
    daysCount,
    firstDay,
    fallbackEndTime,
    fallbackStartTime,
  }).map((item) => {
    const dayRule = (config?.operatingHours || []).find(
      (rule) => rule.dayOfWeek === weekdayKeyFromDate(item.date),
    );
    return {
      ...item,
      startTime: dayRule?.startTime || item.startTime,
      endTime: dayRule?.endTime || item.endTime,
    };
  });
  const breakPeriods = (config?.breakPeriods || [])
    .map((item, index) => {
      const startLocal = toDateTimeLocal(item.startAt);
      const endLocal = toDateTimeLocal(item.endAt);
      const date = dateOnlyFromLocal(startLocal);
      if (!date || !timeOnlyFromLocal(startLocal, "") || !timeOnlyFromLocal(endLocal, "")) {
        return null;
      }
      return {
        id: item.id || createBreakId(index),
        label: String(item.label || "").trim() || `Pausa ${index + 1}`,
        date,
        startTime: timeOnlyFromLocal(startLocal, "12:00"),
        endTime: timeOnlyFromLocal(endLocal, "12:30"),
      };
    })
    .filter(Boolean) as ScheduleBreakPeriodDraft[];
  return {
    dayStartAt: startLocal,
    dayEndAt: endLocal,
    daysCount: String(daysCount),
    dailyStartTime: fallbackStartTime,
    dailyEndTime: fallbackEndTime,
    daySchedules,
    defaultMatchDurationMinutes: String(
      config?.defaultMatchDurationMinutes || 60,
    ),
    minRestMinutes: String(config?.minRestMinutes || 0),
    courtNames: courts.join("\n"),
    breakPeriods,
  };
}

export type ScheduleDraft = ReturnType<typeof createScheduleDraft>;

export function schedulePayloadFromDraft(
  draft: ScheduleDraft,
): Omit<AdminTournamentScheduleConfig, "tournamentId" | "updatedAt"> | null {
  const firstDay = dateOnlyFromLocal(draft.dayStartAt);
  const daysCount = Math.max(1, Number(draft.daysCount || 1));
  const daySchedules =
    Array.isArray(draft.daySchedules) && draft.daySchedules.length > 0
      ? buildScheduleDayWindows({
          daysCount,
          firstDay,
          fallbackEndTime: draft.dailyEndTime || "18:00",
          fallbackStartTime: draft.dailyStartTime || "08:00",
          previous: draft.daySchedules,
        })
      : buildScheduleDayWindows({
          daysCount,
          firstDay,
          fallbackEndTime: draft.dailyEndTime || "18:00",
          fallbackStartTime: draft.dailyStartTime || "08:00",
        });
  const lastDay = daySchedules[daySchedules.length - 1]?.date || firstDay;
  const dayStartAt = localDateTimeIso(
    firstDay,
    daySchedules[0]?.startTime || draft.dailyStartTime || "08:00",
  );
  let dayEndAt = localDateTimeIso(
    lastDay,
    daySchedules[daySchedules.length - 1]?.endTime ||
      draft.dailyEndTime ||
      "18:00",
  );
  if (
    dayStartAt &&
    dayEndAt &&
    new Date(dayEndAt).getTime() <= new Date(dayStartAt).getTime()
  ) {
    dayEndAt = localDateTimeIso(
      addLocalDays(lastDay, 1),
      daySchedules[daySchedules.length - 1]?.endTime ||
        draft.dailyEndTime ||
        "18:00",
    );
  }
  const names = draft.courtNames
    .split(/\r?\n/)
    .map((name) => name.trim())
    .filter(Boolean);
  if (!dayStartAt || !dayEndAt || names.length === 0) return null;
  return {
    dayStartAt,
    dayEndAt,
    courtNames: names,
    defaultMatchDurationMinutes: Number(
      draft.defaultMatchDurationMinutes || 60,
    ),
    minRestMinutes: Number(draft.minRestMinutes || 0),
    breakPeriods: (draft.breakPeriods || [])
      .map((item, index) => {
        const label = String(item.label || "").trim() || `Pausa ${index + 1}`;
        const startAt = localDateTimeIso(item.date, item.startTime);
        let endAt = localDateTimeIso(item.date, item.endTime);
        if (startAt && endAt && new Date(endAt).getTime() <= new Date(startAt).getTime()) {
          endAt = localDateTimeIso(addLocalDays(item.date, 1), item.endTime);
        }
        if (!startAt || !endAt) return null;
        return {
          id: item.id || createBreakId(index),
          label,
          startAt,
          endAt,
        };
      })
      .filter(Boolean) as NonNullable<AdminTournamentScheduleConfig["breakPeriods"]>,
    operatingHours: daySchedules.map((item) => ({
      dayOfWeek: weekdayKeyFromDate(item.date),
      startTime: item.startTime || draft.dailyStartTime || "08:00",
      endTime: item.endTime || draft.dailyEndTime || "18:00",
    })),
    roundPolicy: "strict",
  };
}

export function stablePayload(value: unknown) {
  return JSON.stringify(value);
}

export function slotCourtSelection(slot: AdminTournamentScheduleSlot) {
  const courtName = String(slot.courtName || "").trim();
  return !courtName || courtName === UNASSIGNED_COURT ? "" : courtName;
}
